import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { breadcrumbLd, ORG, T, useSeoHead } from "@/store/seo/seoHead";
import { LangSwitch, spl } from "@/store/seo/ReferencePage";

const guideIndexUrls = {
  en: "/en/guides/",
  es: "/es/guias/",
};

const topicLabel = {
  troubleshooting: ["Troubleshooting", "Diagnóstico"],
  maintenance: ["Maintenance", "Mantenimiento"],
  identification: ["Part identification", "Identificación de pieza"],
  how_to: ["How-to", "Guía práctica"],
};

function guideUrl(guide, lang) {
  return guide.urls?.[lang] || `${guideIndexUrls[lang]}${guide.slug}/`;
}

function indexCrumbs(lang) {
  return [
    { name: "Second Life", url: `/${lang}/` },
    { name: T(lang, "Guides", "Guías"), url: guideIndexUrls[lang] },
  ];
}

function sourceText(source) {
  const title = source.title || "";
  const model = source.model || "";
  return model && !title.toLowerCase().includes(model.toLowerCase())
    ? `${title} · ${model}`
    : title;
}

export function GuideIndexPage() {
  const { lang = "en" } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const crumbs = indexCrumbs(lang);
  useEffect(() => {
    api.get("/guides/public")
      .then(({ data: response }) => setData(response))
      .catch(() => setError(true));
  }, []);
  const items = data?.items || [];
  const title = T(
    lang,
    "Appliance repair & part guides | Second Life",
    "Guías de reparación y piezas | Second Life",
  );
  const description = T(
    lang,
    "Verified appliance part identification, maintenance and troubleshooting guides.",
    "Guías verificadas de identificación, mantenimiento y diagnóstico de piezas.",
  );
  useSeoHead({
    title,
    description,
    urls: guideIndexUrls,
    lang,
    robots: data && !items.length ? "noindex, follow" : "index, follow",
    jsonLd: [ORG, breadcrumbLd(crumbs)],
  });
  if (error) {
    return (
      <div className="p-8 font-mono text-xs text-neutral-600" data-testid="guides-notfound">
        {T(lang, "Guides are unavailable.", "Las guías no están disponibles.")}
      </div>
    );
  }
  if (!data) {
    return <div className="p-8 font-mono text-xs text-neutral-500">…</div>;
  }
  return (
    <section className="mx-auto max-w-6xl py-2" data-testid="guides-index-page">
      <div className="flex items-start justify-between gap-4">
        <nav className="mb-4 font-mono text-[11px] uppercase tracking-widest text-neutral-500">
          <Link to={spl(lang, `/${lang}/`)}>{T(lang, "Second Life", "Second Life")}</Link>
          <span className="mx-1">/</span>
          <span className="text-neutral-700">{T(lang, "Guides", "Guías")}</span>
        </nav>
        <LangSwitch lang={lang} urls={guideIndexUrls} />
      </div>
      <p className="font-mono text-[10px] uppercase tracking-widest text-emerald-700">
        {T(lang, "Verified Library knowledge", "Conocimiento verificado de Biblioteca")}
      </p>
      <h1 className="mt-2 font-display text-3xl font-black uppercase sm:text-4xl" data-testid="guides-h1">
        {T(lang, "Appliance guides", "Guías para electrodomésticos")}
      </h1>
      <p className="mt-3 max-w-3xl text-sm text-neutral-700" data-testid="guides-intro">
        {T(
          lang,
          "Practical information for identifying parts, maintaining appliances and checking common issues. "
            + "Every guide is reviewed before publication and points you back to exact part numbers.",
          "Información práctica para identificar piezas, mantener electrodomésticos y revisar fallas comunes. "
            + "Cada guía se revisa antes de publicarse y te dirige a números de parte exactos.",
        )}
      </p>
      {!items.length && (
        <div className="mt-8 border border-dashed border-neutral-300 p-6 font-mono text-xs text-neutral-600" data-testid="guides-empty">
          {T(
            lang,
            "New verified guides are being prepared. Browse available parts while we add them.",
            "Estamos preparando nuevas guías verificadas. Mientras tanto, explora las piezas disponibles.",
          )}
          <Link
            to={spl(lang, "/search")}
            className="mt-3 block text-emerald-700 hover:underline"
            data-testid="guides-empty-search-link"
          >
            {T(lang, "Browse available parts →", "Explorar piezas disponibles →")}
          </Link>
        </div>
      )}
      <div className="mt-8 grid gap-4 md:grid-cols-2" data-testid="guides-grid">
        {items.map((guide) => (
          <Link
            key={guide.id}
            to={spl(lang, guideUrl(guide, lang))}
            className="border border-neutral-200 p-5 transition-[border-color,transform] hover:-translate-y-0.5 hover:border-emerald-600"
            data-testid={`guide-card-${guide.slug}`}
          >
            <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-700">
              {(topicLabel[guide.topic] || topicLabel.how_to)[lang === "es" ? 1 : 0]}
            </div>
            <h2 className="mt-2 text-xl font-bold text-neutral-900">
              {guide.title?.[lang] || guide.title?.en}
            </h2>
            <p className="mt-2 text-sm text-neutral-700">
              {guide.summary?.[lang] || guide.summary?.en}
            </p>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
              {guide.models?.length
                ? guide.models.join(" · ")
                : guide.appliance || T(lang, "Appliance", "Electrodoméstico")}
            </p>
          </Link>
        ))}
      </div>
    </section>
  );
}

export function GuidePage() {
  const { lang = "en", slug } = useParams();
  const [guide, setGuide] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    api.get(`/guides/public/${slug}`)
      .then(({ data }) => setGuide(data))
      .catch(() => setError(true));
  }, [slug]);
  const crumbs = useMemo(() => {
    if (!guide) return [];
    return [...indexCrumbs(lang), { name: guide.title?.[lang] || guide.title?.en, url: guideUrl(guide, lang) }];
  }, [guide, lang]);
  const title = guide?.title?.[lang] || guide?.title?.en || "";
  const description = guide?.summary?.[lang] || guide?.summary?.en || "";
  const article = guide
    ? {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: title,
        description,
        dateModified: guide.updated_at,
        mainEntityOfPage: `https://secondlifeappliances.com${guideUrl(guide, lang)}`,
        publisher: ORG,
      }
    : null;
  useSeoHead({
    title: guide ? `${title} | Second Life` : "",
    description,
    urls: guide?.urls,
    lang,
    robots: error ? "noindex, follow" : "index, follow",
    jsonLd: guide ? [ORG, breadcrumbLd(crumbs), article] : [],
  });
  if (error) {
    return (
      <div className="p-8 font-mono text-xs text-neutral-600" data-testid="guides-notfound">
        {T(lang, "Guide not found.", "Guía no encontrada.")}
      </div>
    );
  }
  if (!guide) {
    return <div className="p-8 font-mono text-xs text-neutral-500">…</div>;
  }
  const body = guide.body?.[lang] || guide.body?.en || "";
  return (
    <article className="mx-auto max-w-3xl py-2" data-testid="guide-page">
      <div className="flex items-start justify-between gap-4">
        <nav className="mb-4 font-mono text-[11px] uppercase tracking-widest text-neutral-500" data-testid="guide-breadcrumbs">
          <Link to={spl(lang, `/${lang}/`)}>{T(lang, "Second Life", "Second Life")}</Link>
          <span className="mx-1">/</span>
          <Link to={spl(lang, guideIndexUrls[lang])}>{T(lang, "Guides", "Guías")}</Link>
          <span className="mx-1">/</span>
          <span className="text-neutral-700">{title}</span>
        </nav>
        <LangSwitch lang={lang} urls={guide.urls} />
      </div>
      <p className="font-mono text-[10px] uppercase tracking-widest text-emerald-700" data-testid="guide-topic">
        {(topicLabel[guide.topic] || topicLabel.how_to)[lang === "es" ? 1 : 0]}
      </p>
      <h1 className="mt-2 font-display text-3xl font-black uppercase sm:text-4xl" data-testid="guide-h1">
        {title}
      </h1>
      <p className="mt-4 border-l-2 border-emerald-600 pl-4 text-base text-neutral-700" data-testid="guide-summary">
        {description}
      </p>
      <div className="mt-8 space-y-4 whitespace-pre-line text-sm leading-7 text-neutral-800" data-testid="guide-body">
        {body}
      </div>
      <section className="mt-10 border-t border-neutral-200 pt-5" data-testid="guide-sources">
        <h2 className="font-mono text-[10px] uppercase tracking-widest text-neutral-600">
          {T(lang, "Verified sources", "Fuentes verificadas")}
        </h2>
        <ul className="mt-2 space-y-1 text-xs text-neutral-700">
          {guide.sources.map((source) => (
            <li key={`${source.kind}-${source.id}`} data-testid={`guide-source-${source.id}`}>
              {sourceText(source)}
            </li>
          ))}
        </ul>
      </section>
      <Link
        to={spl(lang, guideIndexUrls[lang])}
        className="mt-8 inline-block font-mono text-[11px] uppercase text-emerald-700 hover:underline"
        data-testid="guide-back-link"
      >
        ← {T(lang, "All guides", "Todas las guías")}
      </Link>
    </article>
  );
}